import { MenuItem } from '@blueprintjs/core'
import { set } from 'lodash/fp'
import {
  CultureStatusFilter,
  CultureStatusFilterDefinition,
  FilterDefinition,
  PlateFilter,
  PlateStatusFilter,
  PlateStatusFilterDefinition,
} from '~/api/backend/pages/culturesPageAPI'
import Checkbox from '~/components/Checkbox'
import { CustomCultureStatus } from '~/utils/cultureStatus'
import { snakeCaseToTitleCase } from '~/utils/string'
import { CultureStatusText } from './CultureStatusText'
import cs from './filter_menu_item.scss'
import PlateStatusText from './PlateStatusText'

function FilterMenuItem({
  filter,
  existingFilter,
  setFilter,
  openFilterModal,
}: {
  filter: FilterDefinition
  existingFilter?: PlateFilter
  setFilter: (filterField: string, filter: PlateFilter | null) => void
  openFilterModal: (filterField: string) => void
}) {
  if (!filter || !filter.field) {
    return null
  }

  if (filter.field === 'culture_status') {
    return (
      <CultureStatusFilterMenuItem
        filter={filter}
        existingFilter={existingFilter as CultureStatusFilter}
        setFilter={setFilter}
      />
    )
  }

  if (filter.field === 'plate_status') {
    return (
      <PlateStatusFilterMenuItem
        filter={filter as PlateStatusFilterDefinition}
        existingFilter={existingFilter as PlateStatusFilter}
        setFilter={setFilter}
      />
    )
  }

  return (
    <MenuItem
      className={cs.item}
      key={filter.field}
      text={snakeCaseToTitleCase(filter.field)}
      onClick={() => openFilterModal(filter.field as string)}
    />
  )
}

function CultureStatusFilterMenuItem({
  filter,
  existingFilter,
  setFilter,
}: {
  filter: CultureStatusFilterDefinition
  existingFilter?: CultureStatusFilter
  setFilter: (filterField: string, filter: PlateFilter | null) => void
}) {
  if (!filter.operator || !filter.field || !filter.operator.values) {
    return null
  }
  const toggleFilterWithValue = (value: CustomCultureStatus['name']) => {
    if (!existingFilter) {
      setFilter('culture_status', {
        field: 'culture_status',
        operator: {
          operator_type: 'is_any_of_culture_status',
          values: [value],
        },
      })
    } else {
      const newValues = existingFilter.operator.values.includes(value)
        ? existingFilter.operator.values.filter(v => v !== value)
        : [...existingFilter.operator.values, value]

      if (newValues.length === 0) {
        // If no values left, remove the filter
        setFilter('culture_status', null)
      } else {
        setFilter('culture_status', set('operator.values', newValues, existingFilter))
      }
    }
  }
  return (
    <MenuItem
      className={cs.item}
      key={filter.field}
      text={snakeCaseToTitleCase(filter.field)}
    >
      {filter.operator.values.map(value => (
        <MenuItem
          className={cs.subItem}
          key={value}
          text={
            <div
              className={cs.checkboxMenuItem}
              onClick={() => toggleFilterWithValue(value)}
            >
              <Checkbox
                checked={
                  existingFilter && existingFilter.operator.values.includes(value)
                }
                className={cs.checkbox}
              />
              <CultureStatusText status={value} />
            </div>
          }
          shouldDismissPopover={false}
        />
      ))}
    </MenuItem>
  )
}

function PlateStatusFilterMenuItem({
  filter,
  existingFilter,
  setFilter,
}: {
  filter: PlateStatusFilterDefinition
  existingFilter?: PlateStatusFilter
  setFilter: (filterField: string, filter: PlateFilter | null) => void
}) {
  if (!filter.operator || !filter.field || !filter.operator.values) {
    return null
  }
  const toggleFilterWithValue = (value: 'checked_in' | 'checked_out') => {
    if (!existingFilter) {
      setFilter('plate_status', {
        field: 'plate_status',
        operator: {
          operator_type: 'is_any_of_plate_status',
          values: [value],
        },
      })
    } else {
      const newValues = existingFilter.operator.values.includes(value)
        ? existingFilter.operator.values.filter(v => v !== value)
        : [...existingFilter.operator.values, value]

      if (newValues.length === 0) {
        // If no values left, remove the filter
        setFilter('plate_status', null)
      } else {
        setFilter('plate_status', set('operator.values', newValues, existingFilter))
      }
    }
  }
  return (
    <MenuItem
      className={cs.item}
      key={filter.field}
      text={snakeCaseToTitleCase(filter.field)}
    >
      {filter.operator.values.map(value => (
        <MenuItem
          className={cs.subItem}
          key={value}
          text={
            <div
              className={cs.checkboxMenuItem}
              onClick={() => toggleFilterWithValue(value)}
            >
              <Checkbox
                checked={
                  existingFilter && existingFilter.operator.values.includes(value)
                }
                className={cs.checkbox}
              />
              <PlateStatusText status={value} />
            </div>
          }
          shouldDismissPopover={false}
        />
      ))}
    </MenuItem>
  )
}

export default FilterMenuItem
